import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction
} from 'mobx';

import { generateId } from 'widget/utils/generateId';
import {
  getImageDimensions,
  getVideoThumbnail
} from 'widget/utils/mediaPreview';
import type { SendAttachment } from 'widget/types/messages';

import {
  MAX_FILE_SIZE,
  MAX_ATTACHMENTS_PER_MESSAGE,
  PREVIEW_MIME_TYPES,
  AttachmentStatus,
  AttachmentErrorCode,
  attachmentErrorMessages,
  attachmentErrorToast,
  ATTACHMENTS_LIMIT_TOAST,
  backendErrorCodes,
  Attachment,
  UploadAttachmentFn,
  OnErrorFn
} from './types';

type PrivateFields = '_attachments';

const isRetryableError = (errorCode?: AttachmentErrorCode): boolean => {
  return errorCode === AttachmentErrorCode.uploadFailed;
};

export class AttachmentsStore {
  private _attachments: Attachment[] = [];
  private _abortControllers: Map<string, AbortController> = new Map();
  private _onUpload?: UploadAttachmentFn;
  private _onError?: OnErrorFn;

  constructor(onUpload?: UploadAttachmentFn, onError?: OnErrorFn) {
    this._onUpload = onUpload;
    this._onError = onError;

    makeObservable<AttachmentsStore, PrivateFields>(this, {
      _attachments: observable.ref,

      attachments: computed,
      validAttachments: computed,
      hasAttachments: computed,
      hasValidAttachments: computed,
      hasUploadingAttachments: computed,

      addFiles: action,
      removeAttachment: action,
      clearAttachments: action,
      retryUpload: action
    });
  }

  setUploadFn = (fn?: UploadAttachmentFn): void => {
    this._onUpload = fn;
  };

  setErrorFn = (fn?: OnErrorFn): void => {
    this._onError = fn;
  };

  get attachments(): Attachment[] {
    return this._attachments;
  }

  get validAttachments(): Attachment[] {
    return this._attachments.filter(
      (attachment) => attachment.status === AttachmentStatus.READY
    );
  }

  get hasAttachments(): boolean {
    return this._attachments.length > 0;
  }

  get hasValidAttachments(): boolean {
    return this._attachments.some(
      (attachment) => attachment.status === AttachmentStatus.READY
    );
  }

  get hasUploadingAttachments(): boolean {
    return this._attachments.some(
      (attachment) => attachment.status === AttachmentStatus.UPLOADING
    );
  }

  addFiles = (files: FileList | File[]): void => {
    const currentCount = this._attachments.filter(
      (a) => a.status !== AttachmentStatus.ERROR
    ).length;
    const availableSlots = MAX_ATTACHMENTS_PER_MESSAGE - currentCount;
    const fileArray = Array.from(files);

    if (availableSlots <= 0) {
      this._onError?.(ATTACHMENTS_LIMIT_TOAST);
      return;
    }

    const hasExcessFiles = fileArray.length > availableSlots;
    const filesToAdd = fileArray.slice(0, availableSlots);

    const newAttachments: Attachment[] = filesToAdd.map((file) => {
      const errorCode = this._validateFile(file);

      if (errorCode) {
        return {
          id: generateId(),
          file,
          name: file.name,
          size: file.size,
          mimeType: file.type,
          status: AttachmentStatus.ERROR,
          errorCode,
          error: attachmentErrorMessages[errorCode]
        };
      }

      const previewUrl = this._createPreviewUrl(file);
      const status = this._onUpload
        ? AttachmentStatus.UPLOADING
        : AttachmentStatus.READY;

      return {
        id: generateId(),
        file,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        status,
        previewUrl
      };
    });

    runInAction(() => {
      this._attachments = [...this._attachments, ...newAttachments];
    });

    const firstError = newAttachments.find(
      (attachment) => attachment.status === AttachmentStatus.ERROR
    );

    if (hasExcessFiles) {
      this._onError?.(ATTACHMENTS_LIMIT_TOAST);
    } else if (firstError?.errorCode) {
      this._onError?.(attachmentErrorToast[firstError.errorCode]);
    }

    if (this._onUpload) {
      const toUpload = newAttachments.filter(
        (a) => a.status === AttachmentStatus.UPLOADING
      );
      toUpload.forEach((attachment) =>
        this._uploadFileWithDimensions(attachment)
      );
    }
  };

  private async _uploadFileWithDimensions(
    attachment: Attachment
  ): Promise<void> {
    const imageDimensions = await getImageDimensions(attachment.file);

    if (imageDimensions) {
      runInAction(() => {
        this._updateAttachment(attachment.id, {
          width: imageDimensions.width,
          height: imageDimensions.height
        });
      });
    } else {
      const videoData = await getVideoThumbnail(attachment.file);

      if (videoData) {
        runInAction(() => {
          this._updateAttachment(attachment.id, {
            previewUrl: videoData.thumbnailUrl,
            width: videoData.width,
            height: videoData.height
          });
        });
      }
    }

    await this._uploadFile(attachment);
  }

  retryUpload = (id: string): void => {
    const attachment = this._attachments.find((a) => a.id === id);

    if (
      !attachment ||
      attachment.status !== AttachmentStatus.ERROR ||
      !this._onUpload
    ) {
      return;
    }

    if (!isRetryableError(attachment.errorCode)) {
      return;
    }

    this._updateAttachment(id, {
      status: AttachmentStatus.UPLOADING,
      error: undefined,
      errorCode: undefined
    });

    const updatedAttachment = this._attachments.find((a) => a.id === id);

    if (updatedAttachment) {
      this._uploadFile(updatedAttachment);
    }
  };

  removeAttachment = (id: string): void => {
    const attachment = this._attachments.find((a) => a.id === id);

    if (attachment) {
      this._abortUpload(id);
      this._revokePreviewUrl(attachment);
    }

    this._attachments = this._attachments.filter((a) => a.id !== id);
  };

  clearAttachments = (): void => {
    this._attachments.forEach((attachment) => {
      this._abortUpload(attachment.id);
      this._revokePreviewUrl(attachment);
    });

    this._attachments = [];
    this._abortControllers.clear();
  };

  getAttachmentsForSend(): SendAttachment[] {
    return this.validAttachments
      .filter((a) => a.serverAttachmentId !== undefined)
      .map((a) => ({
        id: a.serverAttachmentId!,
        filename: a.name,
        size: a.size,
        type: a.mimeType,
        url: a.serverUrl!,
        preview_url: a.serverPreviewUrl,
        width: a.width,
        height: a.height
      }));
  }

  private async _uploadFile(attachment: Attachment): Promise<void> {
    if (!this._onUpload) {
      return;
    }

    const abortController = new AbortController();
    this._abortControllers.set(attachment.id, abortController);

    let response;

    try {
      response = await this._onUpload(attachment.file, abortController.signal);
    } catch {
      if (abortController.signal.aborted) {
        return;
      }

      this._handleUploadError(attachment.id, AttachmentErrorCode.uploadFailed);

      return;
    }

    this._abortControllers.delete(attachment.id);

    if (response.isError) {
      const errorData = 'data' in response ? response.data : undefined;
      const errorCode = this._mapBackendError(errorData);
      this._handleUploadError(attachment.id, errorCode);

      return;
    }

    const serverData = response.data.data;

    runInAction(() => {
      this._updateAttachment(attachment.id, {
        status: AttachmentStatus.READY,
        serverAttachmentId: serverData._id,
        serverUrl: serverData.url,
        serverPreviewUrl: serverData.preview_url
      });
    });
  }

  private _handleUploadError(id: string, errorCode: AttachmentErrorCode): void {
    this._abortControllers.delete(id);

    runInAction(() => {
      this._updateAttachment(id, {
        status: AttachmentStatus.ERROR,
        errorCode,
        error: attachmentErrorMessages[errorCode]
      });
    });

    this._onError?.(attachmentErrorToast[errorCode]);
  }

  private _abortUpload(id: string): void {
    const controller = this._abortControllers.get(id);
    if (controller) {
      controller.abort();
      this._abortControllers.delete(id);
    }
  }

  private _mapBackendError(errorData: unknown): AttachmentErrorCode {
    if (typeof errorData === 'string') {
      if (errorData === backendErrorCodes.ENTITY_TOO_BIG) {
        return AttachmentErrorCode.fileTooLarge;
      }
    }
    return AttachmentErrorCode.uploadFailed;
  }

  private _updateAttachment(id: string, patch: Partial<Attachment>): void {
    this._attachments = this._attachments.map((a) =>
      a.id === id ? { ...a, ...patch } : a
    );
  }

  private _validateFile(file: File): AttachmentErrorCode | null {
    if (file.size > MAX_FILE_SIZE) {
      return AttachmentErrorCode.fileTooLarge;
    }

    return null;
  }

  private _createPreviewUrl(file: File): string | undefined {
    if (PREVIEW_MIME_TYPES.includes(file.type)) {
      return URL.createObjectURL(file);
    }

    return undefined;
  }

  private _revokePreviewUrl(attachment: Attachment): void {
    if (attachment.previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  }
}
